import { useState } from "react";
import { useCaseContext } from "../../context/CaseContext";
import { getHeaders } from "../../utils/authHelpers";
import { FetchedReportData } from "../../types";
import { prepareReportFormData } from "../../utils/reportSubmissionUtils";
import { getApiUrl } from "../../utils/api";
import devLogger from "../../utils/devLogger";

export const useSubmitReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [returnedData, setReturnedData] = useState<
    FetchedReportData | undefined
  >(undefined);
  const { resetCaseData } = useCaseContext();

  // Get context data
  const contextData = useCaseContext();

  const submitReport = async (setStep: (step: number) => void) => {
    setLoading(true);
    setError(null);

    try {
      // Prepare form data with case information and files
      const formData = await prepareReportFormData(contextData);

      // Log form data entries for debugging
      // if (process.env.NODE_ENV !== "production") {
      //   const formDataObj = Object.fromEntries(formData.entries());
      //   devLogger.log("Form data prepared for submission:", formDataObj);
      // }

      // Get headers but remove Content-Type for proper multipart/form-data handling
      const headers = getHeaders({ omitContentType: true });

      // Send the formData to the API
      const response = await fetch(getApiUrl("/report/create"), {
        method: "POST",
        headers: headers,
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setReturnedData(data);

        resetCaseData();
        devLogger.log("Report created successfully:", data);
        setStep(11);
      } else {
        // Try to get detailed error information
        let errorMessage =
          "We encountered an error while creating your report. Please try again.";
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If we can't parse the error as JSON, try to get it as text
          try {
            const errorText = await response.text();
            devLogger.error(
              "Failed to create report:",
              response.statusText,
              errorText
            );
          } catch {
            devLogger.error("Failed to create report:", response.statusText);
          }
        }

        setError(errorMessage);
      }
    } catch (error) {
      devLogger.error("Error creating report:", error);
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return { submitReport, loading, error, setError, returnedData };
};
